import React, { useContext, useEffect, useState } from 'react';
import PageTitle from '../../../components/page-title/PageTitle';
import { useTenantService } from '../../../hooks/services/useTenantService';
import { useToastrVanila } from '../../../hooks/util/useToastrVanila';
import IFormError from '../../../interfaces/IFormError';
import IPage from '../../../interfaces/IPage';
import ITenant from '../../../interfaces/ITenant';
import { Container, Form, LatenessContainer } from './TenantForm.styled';
import * as yup from 'yup';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useValidateSchema } from '../../../hooks/providers/useValidateSchema';
import ThemeOptionContext from '../../../context/theme-option/ThemeOptionContext';
import IValidationOption from '../../../interfaces/IValidationOption';
import CompaniesList from '../../campanies/companies-list/CompaniesList';
import { hasAccessToAction } from '../../../util/hasAccessTo';
import UserContext from '../../../context/user/UserContext';

interface TenantProps extends IPage { }

const initialTenant: ITenant = {
  id: '',
  companyName: '',
  companyAddress: '',
  email: '',
  password: '',
  useWhatsApp: false,
  whatsAppInstanceId: '',
  baseURLWhatsAppAPI: '',
  whatsAppToken: '',
  useTimeClocking: false,
  allowsAdjustTimeClock: false,
  whatsappClientToken: '',
  useAcessUserTypeDocuments: false,
  useUserAccessScope: false,
  showCIDForManager: false,
  useMeta: false,
  baseURLMetaApi: '',
  metaAppInstanceId: '',
  metaAppBearerToken: ''
};

const TenantForm = (props: TenantProps) => {
  window.document.title = props.title;
  const [tenant, setTenant] = useState<ITenant>({ ...initialTenant });
  const [errors, setErrors] = useState<IFormError | null>(null);
  const [loading, setLoading] = useState(false);
  const [validationOptions, setValidationOptions] = React.useState<IValidationOption[]>([]);
  const [showWhatsappInstanteId, setShowWhatsappInstanceId] = useState(false);
  const [showWhatsappToken, setShowWhatsappToken] = useState(false);
  const [showWhatsappClientToken, setShowWhatsappClientToken] = useState(false);
  const [value, setValue] = React.useState(0);

  const { state: themeState } = useContext(ThemeOptionContext);
  const request = useTenantService();
  const toastr = useToastrVanila();
  const valitation = useValidateSchema();

  const { state } = useContext(UserContext);
  const { resourcesWithActions } = state;
        
  const isEdit = hasAccessToAction(
    ["parametros-empresa-atestado-web"],
    resourcesWithActions,
    "Editar"
  );

  const schema = yup.object({
    email: yup
      .string()
      .email('Esse não parece um válido. Informe um e-mail da plataforma SeniorX pra rotinas de integração.')
      .nullable(),
    companyName: yup.string(),
    // .required('Campo obrigatório. Informe a razão social ou nome fantasia. Será utilizado em e-mails e mensagens.'),
    companyAddress: yup.string(),
    // .required('Campo obrigatório. Informe o endereço. Será utilizado em e-mails e mensagens.'),
    useWhatsApp: yup.boolean(),
    whatsAppInstanceId: yup.string().test((field, schema) => {
      if (!field && schema.parent.useWhatsApp) {
        return schema.createError({
          message: `Campo obrigatório. Informe o ID da instância do whatsapp obtido junto á Senior Norte PR.`,
        });
      }
      return true;
    }),
    whatsAppToken: yup.string().test((field, schema) => {
      if (!field && schema.parent.useWhatsApp) {
        return schema.createError({
          message: `Campo obrigatório. Informe o token do whatsapp obtido junto á Senior Norte PR.`,
        });
      }
      return true;
    }),
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const listValidationOptions = async (): Promise<void> => {
    setLoading(true);
    await request
      .listValidationOptions()
      .then(result => {
        setValidationOptions(result);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  const getTenant = async () => {
    setLoading(true);
    await request
      .getTenant()
      .then(result => {
        setTenant({ ...result });
        setLoading(false);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  const saveTenant = async () => {
    setLoading(true);
    await request
      .updateTenant(tenant.id, {
        id: tenant.id,
        email: tenant.email,
        password: tenant.password,
        companyName: tenant.companyName,
        companyAddress: tenant.companyAddress,
        useWhatsApp: tenant.useWhatsApp,
        whatsAppInstanceId: tenant.whatsAppInstanceId,
        baseURLWhatsAppAPI: tenant.baseURLWhatsAppAPI,
        whatsAppToken: tenant.whatsAppToken,
        useTimeClocking: tenant.useTimeClocking,
        validationOptionId: tenant.validationOptionId,
        hoursOfLateness: tenant.hoursOfLateness,
        validateLatenessFromBackoffice: tenant.validateLatenessFromBackoffice,
        useUserAccessScope: tenant.useUserAccessScope,
        whatsappClientToken: tenant.whatsappClientToken,
        formatForRecordingNumberOfHoursId: tenant.formatForRecordingNumberOfHoursId,
        allowsAdjustTimeClock: tenant.allowsAdjustTimeClock,
        validationOptionAllowYear: tenant.validationOptionAllowYear,
        documentTypeDisplayMode: tenant.documentTypeDisplayMode,
        useAcessUserTypeDocuments: tenant.useAcessUserTypeDocuments,
        showCIDForManager: tenant.showCIDForManager,
        useMeta: tenant.useMeta,
        baseURLMetaApi: tenant.baseURLMetaApi,
        metaAppInstanceId: tenant.metaAppInstanceId,
        metaAppBearerToken: tenant.metaAppBearerToken
      })
      .then(async result => {
        await getTenant();
        setLoading(false);
        toastr.success('Dados atualizados com sucesso');
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    listValidationOptions();
    getTenant();
  }, []);

  const handleCancel = async () => {
    setErrors(null);
    await getTenant();
    setShowWhatsappInstanceId(false);
    setShowWhatsappToken(false);
  };

  const handleSaveClick = async () => {
    const result = await valitation.validate(schema, tenant);
    setErrors({ ...result.errors });
    if (!result.isValid) {
      toastr.error('Verifique os campos inválidos');
      return;
    } else {
      await saveTenant();
    }
  };

  return (
    <Container isDark={themeState.isDark}>
      <PageTitle title="MANUTENÇÃO DE PARÂMETROS DA EMPRESA" />
      {loading && <LinearProgress />}
      <Tabs value={value} onChange={handleTabChange}>
        <Tab label="Gerais" />
        <Tab label="Validações" />
        <Tab label="Empresas" />
      </Tabs>
      <Form>
        {value === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="RAZÃO SOCIAL OU FANTASIA"
                placeholder="Informe a razão social ou nome fantasia. Será utilizado em e-mails e mensagens"
                value={tenant.companyName || ''}
                onChange={e => setTenant({ ...tenant, companyName: e.target.value })}
                error={Boolean(errors?.companyName)}
                helperText={errors?.companyName}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Endereço"
                placeholder="Informe o endereço. Será utilizado em e-mails e mensagens"
                value={tenant.companyAddress || ''}
                onChange={e => setTenant({ ...tenant, companyAddress: e.target.value })}
                error={Boolean(errors?.companyAddress)}
                helperText={errors?.companyAddress}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="E-mail do usuário da Plataforma SeniorX"
                placeholder="Informe um e-mail da plataforma SeniorX pra rotinas de integração"
                value={tenant.email || ''}
                onChange={e => setTenant({ ...tenant, email: e.target.value })}
                error={Boolean(errors?.email)}
                helperText={errors?.email}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password do usuário admin"
                placeholder="Informe a senha do usuário SeniorX pra rotinas de integração"
                value={tenant.password || ''}
                onChange={e => setTenant({ ...tenant, password: e.target.value })}
                error={Boolean(errors?.password)}
                helperText={errors?.password}
                type="password"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="formatfor-recording-number-of-hours">
                  Formato de lançamento de atestado em horas
                </InputLabel>
                <Select
                  labelId="formatfor-recording-number-of-hours"
                  id="formatfor-recording-number-of-hours-select"
                  value={Number(tenant.formatForRecordingNumberOfHoursId)}
                  label="Formato de lançamento de atestado em horas"
                  onChange={e => {
                    setTenant({
                      ...tenant,
                      formatForRecordingNumberOfHoursId: Number(e.target.value),
                    });
                  }}
                >
                  <MenuItem value={1}>Hora inicial e Hora final</MenuItem>
                  <MenuItem value={2}>Hora inicial e Total de Horas</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="formatfor-recording-number-of-hours">
                  Modelo de exibição dos tipos de documentos
                </InputLabel>
                <Select
                  labelId="Document-Type-Display-Mode"
                  id="Document-Type-Display-Mode"
                  value={Number(tenant.documentTypeDisplayMode)}
                  label="Formato de lançamento de atestado em horas"
                  onChange={e => {
                    setTenant({
                      ...tenant,
                      documentTypeDisplayMode: Number(e.target.value),
                    });
                  }}
                >
                  <MenuItem value={1}>Botão de opção</MenuItem>
                  <MenuItem value={2}>Seletor</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                label="Utiliza abrangência de usuários"
                control={
                  <Checkbox
                    onChange={e =>
                      setTenant({
                        ...tenant,
                        useUserAccessScope: e.target.checked,
                      })
                    }
                    checked={tenant.useUserAccessScope}
                  />
                }
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                label="Utiliza abrangência de acesso a documentos por tipo"
                control={
                  <Checkbox
                    onChange={e =>
                      setTenant({
                        ...tenant,
                        useAcessUserTypeDocuments: e.target.checked,
                      })
                    }
                    checked={tenant.useAcessUserTypeDocuments}
                  />
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                label="Exibir CID para gestores"
                control={
                  <Checkbox
                    onChange={e =>
                      setTenant({
                        ...tenant,
                        showCIDForManager: e.target.checked,
                      })
                    }
                    checked={tenant.showCIDForManager}
                  />
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <WhatsAppIcon sx={{ color: '#1bab74', marginRight: 1.5, marginTop: '2px' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                      WhatsApp
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Configurações de acesso
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sx={{m:2}}>
                      <FormControlLabel
                        label="Utilizar PlugZapp"
                        componentsProps={{
                          typography: {
                            sx: { fontWeight: tenant.useWhatsApp ? 700 : 400 },
                          },
                        }}
                        control={
                          <Checkbox
                            onChange={e =>
                              setTenant({
                                ...tenant,
                                useWhatsApp: e.target.checked,
                                useMeta: false,
                              })
                            }
                            color='success'
                            checked={tenant.useWhatsApp}
                          />
                        }
                      />
                      <FormControlLabel
                        label="Utilizar Meta"
                        componentsProps={{
                        typography: {
                          sx: { fontWeight: tenant.useMeta ? 700 : 400 },
                        },
                      }}
                        control={
                          <Checkbox
                            onChange={e =>
                              setTenant({
                                ...tenant,
                                useMeta: e.target.checked,
                                useWhatsApp: false,
                              })
                            }
                            color='success'
                            checked={tenant.useMeta}
                          />
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={tenant.useMeta ? 'Meta URL base' : 'WhatsApp URL base'}
                        placeholder="Informe a URL base da API de envio de mensagem"
                        value={
                          tenant && tenant.useMeta
                            ? (tenant.baseURLMetaApi ?? '')
                            : (tenant.baseURLWhatsAppAPI ?? '')
                        }
                        onChange={e =>
                          setTenant({
                            ...tenant,
                            ...(tenant.useMeta
                              ? { baseURLMetaApi: e.target.value }
                              : { baseURLWhatsAppAPI: e.target.value }),
                          })
                        }
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(errors?.baseURLWhatsAppAPI)}
                        helperText={errors?.baseURLWhatsAppAPI}
                        disabled={!tenant.useWhatsApp && !tenant.useMeta}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={tenant.useMeta ? 'Meta App Instance ID' : 'WhatsApp InstanceID'}
                        placeholder={
                          tenant.useMeta
                            ? 'Informe o ID da instância Meta'
                            : 'Informe o ID da instância do WhatsApp obtido junto a Senior Norte PR'
                        }
                        value={
                          tenant.useMeta
                            ? (tenant.metaAppInstanceId ?? '')
                            : (tenant.whatsAppInstanceId ?? '')
                        }
                        onChange={e =>
                          setTenant({
                            ...tenant,
                            ...(tenant.useMeta
                              ? { metaAppInstanceId: e.target.value }
                              : { whatsAppInstanceId: e.target.value }),
                          })
                        }
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(errors?.whatsAppInstanceId)}
                        helperText={errors?.whatsAppInstanceId}
                        type={showWhatsappInstanteId ? 'text' : 'password'}
                        disabled={!tenant.useWhatsApp && !tenant.useMeta}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                edge="end"
                                onClick={() => setShowWhatsappInstanceId(!showWhatsappInstanteId)}
                              >
                                {!showWhatsappInstanteId ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={tenant.useMeta ? 'Meta Bearer Token' : 'WhatsApp Token'}
                        placeholder={
                          tenant.useMeta
                            ? 'Informe o Bearer Token da Meta'
                            : 'Informe o token do WhatsApp obtido junto a Senior Norte PR'
                        }
                        value={
                          tenant.useMeta
                            ? (tenant.metaAppBearerToken ?? '')
                            : (tenant.whatsAppToken ?? '')
                        }
                        onChange={e =>
                          setTenant({
                            ...tenant,
                            ...(tenant.useMeta
                              ? { metaAppBearerToken: e.target.value }
                              : { whatsAppToken: e.target.value }),
                          })
                        }
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(errors?.whatsAppToken)}
                        helperText={errors?.whatsAppToken}
                        type={showWhatsappToken ? 'text' : 'password'}
                        disabled={!tenant.useWhatsApp && !tenant.useMeta}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                edge="end"
                                onClick={() => setShowWhatsappToken(!showWhatsappToken)}
                              >
                                {!showWhatsappToken ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    {tenant.useWhatsApp && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="WhatsApp Client-Token"
                          placeholder="Informe o Client-Token do WhatsApp obtido junto a Senior Norte PR"
                          value={tenant.whatsappClientToken ?? ''}
                          onChange={e => setTenant({ ...tenant, whatsappClientToken: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                          error={Boolean(errors?.whatsappClientToken)}
                          helperText={errors?.whatsappClientToken}
                          type={showWhatsappClientToken ? 'text' : 'password'}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  edge="end"
                                  onClick={() => setShowWhatsappClientToken(!showWhatsappClientToken)}
                                >
                                  {!showWhatsappClientToken ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <PointOfSaleIcon sx={{ color: '#1975d1', marginRight: 1.5 }}></PointOfSaleIcon>
                  <Box>
                    <Typography sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                      Controle de Ponto
                    </Typography>                  
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Configurações de acesso
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        label="Utiliza controle de ponto"
                        control={
                          <Checkbox
                            onChange={e =>
                              setTenant({
                                ...tenant,
                                useTimeClocking: e.target.checked,
                              })
                            }
                            checked={tenant.useTimeClocking}
                          />
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        label="Permite ajuste de ponto"
                        control={
                          <Checkbox
                            onChange={e =>
                              setTenant({
                                ...tenant,
                                allowsAdjustTimeClock: e.target.checked,
                              })
                            }
                            checked={tenant.allowsAdjustTimeClock}
                          />
                        }
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        )}

        {value === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <LatenessContainer>
                <LatenessContainer style={{ minWidth: '20px', marginRight: '10px' }}>
                  <span>Atestados enviados após</span>
                </LatenessContainer>

                <LatenessContainer>
                  <TextField
                    value={tenant.hoursOfLateness || ''}
                    onChange={e => setTenant({ ...tenant, hoursOfLateness: parseInt(e.target.value) })}
                    type="number"
                    variant="standard"
                    sx={{ width: '50px', marginRight: '10px' }}
                  />
                  <span style={{ marginRight: '10px' }}>Horas</span>
                </LatenessContainer>

                <FormControl variant="standard" sx={{ minWidth: '18px' }}>
                  <Select
                    value={tenant.validationOptionId || ''}
                    onChange={e => setTenant({ ...tenant, validationOptionId: e.target.value as number })}
                  >
                    {validationOptions.map(option => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </LatenessContainer>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="formatfor-recording-number-of-hours">
                  Quando exceder quantidade de documentos permitidos por ano
                </InputLabel>
                <Select
                  labelId="formatfor-recording-number-of-hours"
                  id="formatfor-recording-number-of-hours-select"
                  value={tenant.validationOptionAllowYear}
                  label="Quando exceder quantidade de documentos permitidos por ano"
                  onChange={e => {
                    setTenant({
                      ...tenant,
                      validationOptionAllowYear: Number(e.target.value),
                    });
                  }}
                >
                  <MenuItem value={1}>1 - Criar Alerta</MenuItem>
                  <MenuItem value={2}>2 - Reprovar Automáticamente</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                label="Validar também quando enviado pelo RH"
                control={
                  <Checkbox
                    onChange={e => setTenant({ ...tenant, validateLatenessFromBackoffice: e.target.checked })}
                    checked={tenant.validateLatenessFromBackoffice}
                  />
                }
              />
            </Grid>
          </Grid>
        )}
        {value === 2 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CompaniesList title="Atestado | Empresa" />
            </Grid>
          </Grid>
        )}
      </Form>
      {value !== 2 && (
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          sx={{ marginTop: 5 }}
          spacing={2}
        >
          <Grid item>
            <LoadingButton loading={loading} variant="contained" color="secondary" onClick={handleCancel}>
              Cancelar
            </LoadingButton>
          </Grid>
          <Grid item>
            <LoadingButton loading={loading} variant="contained" color="primary" onClick={handleSaveClick} disabled={!isEdit}>
              Salvar
            </LoadingButton>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default TenantForm;
